import { useEffect } from 'react';

const DESKTOP_MIN = 1024;
const ANIMATION_DURATION_MS = 1600;
const LINE_DURATION_MS = 1300;
const LEAVE_RESET_DELAY_MS = 180;
const OBSERVER_THRESHOLD = 0.18;
const OBSERVER_ROOT_MARGIN = '0px 0px -15% 0px';

/** Heading uses first ~42% of forward track, body the rest */
const HEADING_SCROLL_SHARE = 0.42;

const clamp01 = (value) => Math.min(1, Math.max(0, value));

const smoothstep = (t) => {
  const x = clamp01(t);
  return x * x * (3 - 2 * x);
};

const isDesktopViewport = () => window.innerWidth >= DESKTOP_MIN;

const getLineChars = (lineEl, isRtl) => {
  const chars = [...lineEl.querySelectorAll('.innovation-card-char')];
  if (isRtl) {
    chars.reverse();
  }
  return chars;
};

const collectHeadingAndBodyGroups = (card) => {
  const isRtl = card.classList.contains('innovation-feature-card--fill-rtl');
  const headingLines = [];
  const bodyLines = [];

  card.querySelectorAll('.innovation-card-title-line').forEach((lineEl) => {
    const chars = getLineChars(lineEl, isRtl);
    if (chars.length > 0) {
      headingLines.push(chars);
    }
  });

  card.querySelectorAll('.innovation-card-body-line').forEach((lineEl) => {
    const chars = getLineChars(lineEl, isRtl);
    if (chars.length > 0) {
      bodyLines.push(chars);
    }
  });

  return { headingLines, bodyLines };
};

const applyParallelCharProgress = (lineGroups, progress) => {
  lineGroups.forEach((chars) => {
    const total = chars.length;
    if (total === 0) return;

    const slice = 1 / total;
    chars.forEach((charEl, i) => {
      const start = i * slice;
      const fill = smoothstep(clamp01((progress - start) / slice));
      charEl.style.opacity = fill.toFixed(4);
    });
  });
};

const hideLineGroups = (lineGroups) => {
  lineGroups.forEach((chars) => {
    chars.forEach((charEl) => {
      charEl.style.opacity = '0';
    });
  });
};

const applyLineReveal = (card, lineReveal) => {
  card.style.setProperty('--line-reveal', lineReveal.toFixed(4));
};

const applyForwardTextFromProgress = (headingLines, bodyLines, scrollProgress) => {
  const headingProgress = clamp01(scrollProgress / HEADING_SCROLL_SHARE);
  const bodyProgress = clamp01((scrollProgress - HEADING_SCROLL_SHARE) / (1 - HEADING_SCROLL_SHARE));

  applyParallelCharProgress(headingLines, headingProgress);
  if (scrollProgress <= HEADING_SCROLL_SHARE) {
    hideLineGroups(bodyLines);
  } else {
    applyParallelCharProgress(bodyLines, bodyProgress);
  }
};

const restartAnimationClass = (img, className) => {
  img.classList.remove(className);
  void img.offsetWidth;
  img.classList.add(className);
};

const playImagePop = (img) => {
  img.classList.remove(
    'in-view-out',
    'innovation-card-image--settled-out',
    'innovation-card-image--settled-in',
    'in-view',
  );
  restartAnimationClass(img, 'in-view');
};

const playImageInOnce = (img, state) => {
  if (state.hasPlayedIn || state.animating) return;

  state.animating = true;
  state.hasPlayedIn = true;
  playImagePop(img);
};

const settleImageIn = (img, state) => {
  state.animating = false;
  img.classList.remove('in-view', 'in-view-out');
  img.classList.add('innovation-card-image--settled-in');
};

const resetImage = (img, state) => {
  state.animating = false;
  state.hasPlayedIn = false;
  img.classList.remove(
    'in-view',
    'in-view-out',
    'innovation-card-image--settled-in',
    'innovation-card-image--settled-out',
  );
};

const resetCardAnimation = (card, imageState, playbackState) => {
  const playback = playbackState.get(card);
  if (playback?.rafId != null) {
    window.cancelAnimationFrame(playback.rafId);
  }
  playbackState.delete(card);

  const { headingLines, bodyLines } = collectHeadingAndBodyGroups(card);
  hideLineGroups(headingLines);
  hideLineGroups(bodyLines);
  applyLineReveal(card, 0);

  const img = card.querySelector('.innovation-card-image');
  if (img) {
    const imgState = imageState.get(img) ?? { hasPlayedIn: false, animating: false };
    resetImage(img, imgState);
    imageState.set(img, imgState);
  }
};

const playForwardAnimation = (card, imageState, playbackState) => {
  const existing = playbackState.get(card);
  if (existing?.phase === 'revealed' || existing?.phase === 'forward') {
    return;
  }

  if (existing?.rafId != null) {
    window.cancelAnimationFrame(existing.rafId);
  }

  const { headingLines, bodyLines } = collectHeadingAndBodyGroups(card);
  const img = card.querySelector('.innovation-card-image');
  const imgState = img ? imageState.get(img) : null;

  hideLineGroups(headingLines);
  hideLineGroups(bodyLines);
  applyLineReveal(card, 0);

  if (img && imgState) {
    resetImage(img, imgState);
    playImageInOnce(img, imgState);
  }

  const playback = {
    phase: 'forward',
    startTime: null,
    rafId: null,
  };
  playbackState.set(card, playback);

  const tick = (timestamp) => {
    if (!isDesktopViewport()) return;

    if (playback.startTime == null) {
      playback.startTime = timestamp;
    }

    const elapsed = timestamp - playback.startTime;
    const textProgress = clamp01(elapsed / ANIMATION_DURATION_MS);
    const lineProgress = smoothstep(clamp01(elapsed / LINE_DURATION_MS));

    applyLineReveal(card, lineProgress);
    applyForwardTextFromProgress(headingLines, bodyLines, textProgress);

    if (textProgress < 1) {
      playback.rafId = window.requestAnimationFrame(tick);
      return;
    }

    applyParallelCharProgress(headingLines, 1);
    applyParallelCharProgress(bodyLines, 1);
    applyLineReveal(card, 1);
    playback.phase = 'revealed';
    playback.rafId = null;
  };

  playback.rafId = window.requestAnimationFrame(tick);
};

const resetDesktopStyles = (root, imageState, playbackState) => {
  root.querySelectorAll('.innovation-feature-card').forEach((card) => {
    card.style.removeProperty('--line-reveal');
    resetCardAnimation(card, imageState, playbackState);
    card.querySelectorAll('.innovation-card-char').forEach((char) => {
      char.style.removeProperty('opacity');
    });
  });
};

/**
 * Desktop only: same char/line/image reveal as scroll-scrub, auto-plays on viewport enter.
 */
export default function useInnovationCardScrollReveal(sectionRef) {
  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return undefined;

    const desktopRail = section.querySelector('.innovation-cards-rail--desktop');
    if (!desktopRail) return undefined;

    const cards = [...desktopRail.querySelectorAll('.innovation-feature-card')];
    const imageState = new WeakMap();
    const playbackState = new WeakMap();
    const leaveTimers = new Map();
    let desktopActive = isDesktopViewport();

    const onImageAnimationEnd = (event) => {
      const img = event.currentTarget;
      const state = imageState.get(img);
      if (!state || event.animationName !== 'innovationImagePop') return;
      settleImageIn(img, state);
    };

    desktopRail.querySelectorAll('.innovation-card-image').forEach((img) => {
      imageState.set(img, {
        hasPlayedIn: false,
        animating: false,
      });
      img.addEventListener('animationend', onImageAnimationEnd);
    });

    const scheduleLeaveReset = (card) => {
      const pending = leaveTimers.get(card);
      if (pending) {
        window.clearTimeout(pending);
      }

      const timerId = window.setTimeout(() => {
        leaveTimers.delete(card);
        const rect = card.getBoundingClientRect();
        const vh = window.innerHeight;
        if (rect.bottom <= 0 || rect.top >= vh) {
          resetCardAnimation(card, imageState, playbackState);
        }
      }, LEAVE_RESET_DELAY_MS);

      leaveTimers.set(card, timerId);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        if (!isDesktopViewport()) return;

        entries.forEach((entry) => {
          const card = entry.target;
          const pendingLeave = leaveTimers.get(card);
          if (pendingLeave) {
            window.clearTimeout(pendingLeave);
            leaveTimers.delete(card);
          }

          if (entry.isIntersecting) {
            playForwardAnimation(card, imageState, playbackState);
          } else {
            scheduleLeaveReset(card);
          }
        });
      },
      { threshold: OBSERVER_THRESHOLD, rootMargin: OBSERVER_ROOT_MARGIN },
    );

    const onResize = () => {
      const isDesktop = isDesktopViewport();
      if (!isDesktop && desktopActive) {
        resetDesktopStyles(desktopRail, imageState, playbackState);
        desktopActive = false;
        return;
      }
      desktopActive = isDesktop;
    };

    cards.forEach((card) => observer.observe(card));
    window.addEventListener('resize', onResize, { passive: true });

    return () => {
      observer.disconnect();
      leaveTimers.forEach((timerId) => window.clearTimeout(timerId));
      leaveTimers.clear();
      desktopRail.querySelectorAll('.innovation-card-image').forEach((img) => {
        img.removeEventListener('animationend', onImageAnimationEnd);
      });
      resetDesktopStyles(desktopRail, imageState, playbackState);
      window.removeEventListener('resize', onResize);
    };
  }, [sectionRef]);
}
